import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { AlertTriangle, CheckCircle, XCircle, RefreshCw, Activity, DollarSign, TrendingDown, TrendingUp, Zap } from 'lucide-react';

const API_V2 = `${process.env.REACT_APP_BACKEND_URL}/api/v2`;

const StatusBadge = ({ ok, label }) => (
  <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-mono border ${
    ok ? 'border-green-800 bg-green-900/30 text-green-400' : 'border-red-800 bg-red-900/30 text-red-400'
  }`} data-testid={`status-${label.toLowerCase().replace(/\s/g, '-')}`}>
    {ok ? <CheckCircle className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
    {label}
  </span>
);

const BlockerChip = ({ text }) => (
  <span className="inline-block px-2 py-0.5 text-xs font-mono border border-red-700 bg-red-900/20 text-red-400 mr-1 mb-1">
    {text}
  </span>
);

const ReadyChip = ({ text }) => (
  <span className="inline-block px-2 py-0.5 text-xs font-mono border border-green-700 bg-green-900/20 text-green-400 mr-1 mb-1">
    {text}
  </span>
);

const BotDiagnostics = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);

  const fetchDiagnostics = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API_V2}/user/bot/debug/trading-state`, { withCredentials: true });
      setData(res.data);
      setLastRefresh(new Date());
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Failed to fetch diagnostics');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDiagnostics();
    const interval = setInterval(fetchDiagnostics, 15000);
    return () => clearInterval(interval);
  }, [fetchDiagnostics]);

  if (error) {
    return (
      <Card className="matrix-card rounded-none" data-testid="bot-diagnostics-card">
        <CardContent className="p-6 text-center">
          <AlertTriangle className="h-6 w-6 text-red-400 mx-auto mb-2" />
          <p className="text-red-400 text-sm font-mono">{error}</p>
          <button onClick={fetchDiagnostics} className="mt-3 px-4 py-1 text-xs border border-[#003B00] text-[#008F11] hover:bg-[#003B00]/30 font-mono uppercase">
            Retry
          </button>
        </CardContent>
      </Card>
    );
  }

  if (!data) {
    return (
      <Card className="matrix-card rounded-none" data-testid="bot-diagnostics-card">
        <CardContent className="p-6 text-center">
          <Activity className="h-5 w-5 text-[#008F11] mx-auto animate-pulse" />
          <p className="text-[#008F11] text-xs font-mono mt-2">Loading diagnostics...</p>
        </CardContent>
      </Card>
    );
  }

  const isLive = data.mode === 'live';
  const isRunning = data.status === 'running';

  return (
    <div className="space-y-4" data-testid="bot-diagnostics-card">
      {/* Header with refresh */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <StatusBadge ok={isRunning} label={isRunning ? 'RUNNING' : 'STOPPED'} />
          <StatusBadge ok={isLive} label={isLive ? 'LIVE' : 'SIMULATION'} />
          {data.bleed_pairs?.length > 0 && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-mono border border-yellow-700 bg-yellow-900/30 text-yellow-400">
              <Zap className="h-3 w-3" />
              BLEED: {data.bleed_pairs.join(', ')}
            </span>
          )}
        </div>
        <button
          onClick={fetchDiagnostics}
          disabled={loading}
          className="flex items-center gap-1 px-3 py-1 text-xs border border-[#003B00] text-[#008F11] hover:bg-[#003B00]/30 font-mono uppercase disabled:opacity-50"
          data-testid="refresh-diagnostics-btn"
        >
          <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} />
          {loading ? 'Loading...' : 'Refresh'}
        </button>
      </div>

      {/* Balance & Settings Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="p-3 border border-[#003B00] bg-[#0a0a0a]">
          <div className="flex items-center gap-1 text-[#008F11] text-xs font-mono mb-1">
            <DollarSign className="h-3 w-3" /> USD BALANCE
          </div>
          <div className="text-lg font-mono text-[#00FF41]" data-testid="diag-usd-balance">
            ${data.usd_balance?.toFixed(2)}
          </div>
          {isLive && data.live_balance_sync && (
            <div className={`text-xs font-mono mt-1 ${
              data.live_balance_sync.status === 'SUCCESS' ? 'text-green-500' : 'text-red-400'
            }`}>
              Sync: {data.live_balance_sync.status}
            </div>
          )}
        </div>
        <div className="p-3 border border-[#003B00] bg-[#0a0a0a]">
          <div className="text-[#008F11] text-xs font-mono mb-1">PORTFOLIO VALUE</div>
          <div className="text-lg font-mono text-[#00FF41]" data-testid="diag-portfolio-value">
            ${data.total_portfolio_value?.toFixed(2)}
          </div>
        </div>
        <div className="p-3 border border-[#003B00] bg-[#0a0a0a]">
          <div className="flex items-center gap-1 text-[#008F11] text-xs font-mono mb-1">
            <TrendingDown className="h-3 w-3" /> BUY RSI
          </div>
          <div className="text-lg font-mono text-[#00FF41]">
            &le; {data.settings?.rsi_oversold}
          </div>
          <div className="text-xs text-[#008F11]/60 font-mono">min ${data.settings?.min_trade_usd}</div>
        </div>
        <div className="p-3 border border-[#003B00] bg-[#0a0a0a]">
          <div className="flex items-center gap-1 text-[#008F11] text-xs font-mono mb-1">
            <TrendingUp className="h-3 w-3" /> SELL RSI
          </div>
          <div className="text-lg font-mono text-[#00FF41]">
            &ge; {data.settings?.rsi_overbought}
          </div>
          <div className="text-xs text-[#008F11]/60 font-mono">
            min profit: {data.settings?.min_sell_profit_percent ?? 1}% | hold: {data.settings?.min_hold_minutes ?? 5}m
          </div>
        </div>
      </div>

      {/* Per-pair diagnostics */}
      <Card className="matrix-card rounded-none">
        <CardHeader className="border-b border-[#003B00] p-3">
          <CardTitle className="text-xs uppercase tracking-widest text-[#008F11] flex items-center gap-2">
            <Activity className="h-3.5 w-3.5" />
            PER-PAIR DIAGNOSTICS ({Object.keys(data.pairs || {}).length} pairs)
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {Object.keys(data.pairs || {}).length > 0 ? (
            <div className="divide-y divide-[#003B00]/50">
              {Object.entries(data.pairs).map(([pairName, pair]) => (
                <div key={pairName} className="p-3 hover:bg-[#001a00]/50 transition-colors" data-testid={`diag-pair-${pairName}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-mono text-[#00FF41] font-bold">{pairName}</span>
                      {pair.is_bleed && (
                        <span className="text-xs px-1.5 py-0.5 border border-yellow-700 bg-yellow-900/30 text-yellow-400 font-mono">BLEED</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-mono text-[#008F11]/80">
                        RSI: <span className={`font-bold ${
                          pair.rsi <= (data.settings?.rsi_oversold || 30) ? 'text-green-400' :
                          pair.rsi >= (data.settings?.rsi_overbought || 70) ? 'text-red-400' : 'text-[#00FF41]'
                        }`}>{pair.rsi?.toFixed(1)}</span>
                      </span>
                      <span className="text-xs font-mono text-[#008F11]/60">${pair.price?.toFixed(4)}</span>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {/* Buy status */}
                    <div>
                      <div className="text-xs font-mono text-[#008F11]/60 mb-1">BUY:</div>
                      {pair.can_buy ? (
                        <ReadyChip text="READY TO BUY" />
                      ) : (
                        pair.buy_blockers?.map((b, i) => (
                          <BlockerChip key={i} text={b} />
                        ))
                      )}
                    </div>
                    {/* Sell status */}
                    <div>
                      <div className="text-xs font-mono text-[#008F11]/60 mb-1">SELL:</div>
                      {pair.can_sell ? (
                        <ReadyChip text="READY TO SELL" />
                      ) : (
                        pair.sell_blockers?.map((b, i) => (
                          <BlockerChip key={i} text={b} />
                        ))
                      )}
                    </div>
                  </div>
                  {/* Balance info */}
                  <div className="mt-1.5 text-xs font-mono text-[#008F11]/50">
                    Holdings: {pair.balance?.toFixed(6)} ({pair.concentration}) | Value: ${pair.value_usd?.toFixed(2)}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-6 text-center text-[#008F11]/50 text-sm font-mono">
              No active pairs configured
            </div>
          )}
        </CardContent>
      </Card>

      {lastRefresh && (
        <div className="text-xs text-[#008F11]/40 font-mono text-right">
          Last updated: {lastRefresh.toLocaleTimeString()} (auto-refreshes every 15s)
        </div>
      )}
    </div>
  );
};

export default BotDiagnostics;
